import { useEffect, useState } from 'react';
import { useCallMetric } from '@/hooks/use-call-metric/use-call-metric';
import { useMetricsChart } from '@/hooks/use-metrics-chart/use-metrics-chart';
import styles from './dashboard.module.scss';
import { CallMetric } from '@/components/call-metric/call-metric';
import Insights from '@/components/insights/insights';
import { Flex } from 'antd';
import {
  ChartParam,
  ChartSmsParam,
  DashboardFilters,
} from '@/services/dashboard-service/dashboard.service.types';
import { CustomFilters } from '@/components/custom-filters/custom-filters.tsx';
import CustomSwitch from '@/components/custom-switch/custom-switch';
import { ActiveType } from '@/components/custom-switch/custom-switch.types';
import { SmsMetric } from '@/components/sms-metric/sms-metric';
import { useSmsMetric } from '@/hooks/use-sms-metric/use-sms-metric';
import { useSmsMetricsChart } from '@/hooks/use-sms-metrics-chart/use-sms-metrics-chart';
import { useInsights } from '@/hooks/use-insights/use-insights.tsx';
import { useUser } from '@/hooks/use-user/use-user';
import { Dayjs } from 'dayjs';
import { useCompanyStore } from '@/store/app.store';
import AiPerformanceSection from '@/components/ai-performance/ai-performance';
import { useAiPerformance } from '@/hooks/use-ai-performance/use-ai-performance';
import { useCompanySearch } from '@/hooks/use-company-search/use-company-search';
import { useCompaniesList } from '@/hooks/use-company/use-company';

export function Dashboard() {
  const { companyName, setCompanyName } = useCompanyStore();

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [chartParam, setChartParam] = useState<ChartParam>();
  const [smsChartParam, setSmsChartParam] = useState<ChartSmsParam>();
  const [activeMetric, setActiveMetric] = useState<ActiveType>('Calls');

  const [dates, setDates] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const { userData, isFetched, isLoading: isLoadingUserData } = useUser();
  const { companies } = useCompaniesList();

  const isCompanyAdmin = userData?.roles.some((role) => role.name === 'company_admin');
  const isBayonne = userData?.company?.name?.toLowerCase() === 'Bayonne'.toLowerCase();
  const defaultCompanyName = isCompanyAdmin ? userData?.default_company : userData?.company?.name;
  const hasMultipleCompanies = (companies?.length ?? 0) > 1;

  const [selectedFilter, setSelectedFilter] = useState<DashboardFilters>({
    time_range: '7d',
    company_name: companyName || (isCompanyAdmin ? userData?.default_company : userData?.company?.name),
    start_time: undefined,
    end_time: undefined,
  });

  const { callMetricData, isPending: callMetricsDataLoading } = useCallMetric(
    {
      ...selectedFilter,
      time_zone: timeZone,
      company_name: isLoadingUserData ? undefined : selectedFilter.company_name,
    },
    isCompanyAdmin
  );

  const { metricsChartData, isLoading: metricsChartDataLoading } = useMetricsChart({
    time_range: selectedFilter.time_range,
    chart_params: chartParam,
    start_time: selectedFilter.start_time,
    end_time: selectedFilter.end_time,
    time_zone: timeZone,
    company_name: isLoadingUserData ? undefined : selectedFilter.company_name,
  });

  const { smsMetricData, isPending: smsMetricsDataLoading } = useSmsMetric(
    {
      ...selectedFilter,
      company_name: isLoadingUserData ? undefined : selectedFilter.company_name,
    },
    isCompanyAdmin
  );

  const { smsMetricsChartData, isLoading: smsMetricsChartDataLoading } = useSmsMetricsChart({
    time_range: selectedFilter.time_range,
    chart_params: smsChartParam,
    start_time: selectedFilter.start_time,
    end_time: selectedFilter.end_time,
    time_zone: timeZone,
    company_name: isLoadingUserData ? undefined : selectedFilter.company_name,
  });

  const { aiPerformanceData, isPending: isAiPerformancePending } = useAiPerformance(
    {
      ...selectedFilter,
      company_name: isLoadingUserData ? undefined : selectedFilter.company_name,
      time_zone: timeZone,
    },
    isCompanyAdmin
  );

  const onClearDateRangeFilter = () => {
    setSelectedFilter((prevState) => ({
      ...prevState,
      time_range: '7d',
      start_time: undefined,
      end_time: undefined,
    }));

    setDates(null);
  };

  const onChangeTimeRangeFilter = (filterValue: DashboardFilters) => {
    setSelectedFilter((prevState) => ({
      ...prevState,
      time_range: filterValue.time_range,
      start_time: undefined,
      end_time: undefined,
    }));
    setDates([null, null]);
  };

  const onChangeDateRangeFilter = (filterValue: {
    time_range: { start_time: string; end_time: string };
  }) => {
    setSelectedFilter((prevState) => ({
      ...prevState,
      time_range: undefined,
      start_time: filterValue.time_range.start_time,
      end_time: filterValue.time_range.end_time,
    }));
  };

  const { insightsData, isPending } = useInsights(
    {
      ...selectedFilter,
      company_name: isLoadingUserData ? undefined : selectedFilter.company_name,
      time_zone: timeZone,
    },
    isCompanyAdmin
  );

  const handleCardClick = (param: ChartParam) => {
    if (param === chartParam) {
      setChartParam(undefined);
    } else {
      setChartParam(param);
    }
  };

  const handleSmsCardClick = (param: ChartSmsParam) => {
    setSmsChartParam((prev) => {
      return param === prev ? undefined : param;
    });
  };

  const getButtonClassName = (param?: ChartParam | ChartSmsParam) => {
    return param === chartParam || param === smsChartParam ? styles.cardButtonActive : '';
  };

  const handleSwitch = (active: ActiveType) => {
    setChartParam(undefined);
    setSmsChartParam(undefined);
    setActiveMetric(active);
  };

  useEffect(() => {
    if (companyName) {
      setSelectedFilter((prevState) => ({
        ...prevState,
        company_name: companyName,
      }));
    } else if (isCompanyAdmin && userData?.default_company) {
      setSelectedFilter((prevState) => ({
        ...prevState,
        company_name: userData.default_company,
      }));
    } else if (userData?.company?.name) {
      setSelectedFilter((prevState) => ({
        ...prevState,
        company_name: prevState.company_name || userData.company?.name,
      }));
    }
  }, [companyName, isCompanyAdmin, userData?.default_company, userData?.company?.name]);

  const handleCompanyChange = (filters: DashboardFilters) => {
    setSelectedFilter(filters);
    setCompanyName(filters.company_name ?? defaultCompanyName);
  };

  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const { companySearchData, companySearchLoading, companySearchError } = useCompanySearch({
    query: companySearchQuery,
  });

  return (
    <Flex vertical>
      <Flex gap={20} align={'center'} className={styles.actionWrapper}>
        <CustomSwitch
          initialActive="Calls"
          onSwitch={handleSwitch}
          className={styles.toggleButton}
        />
        {isFetched && (
          <CustomFilters
            companiesList={companies?.map((company) => company.name) || []}
            filters={selectedFilter}
            defaultCompanyValue={defaultCompanyName}
            onChangeFilters={handleCompanyChange}
            onSearchQueryChange={setCompanySearchQuery}
            searchResults={companySearchData}
            isSearchLoading={companySearchLoading}
            searchError={companySearchError}
            disabled={isCompanyAdmin && !hasMultipleCompanies}
          />
        )}
      </Flex>
      {activeMetric === 'Calls' ? (
        <CallMetric
          dates={dates}
          setDates={setDates}
          onDateRangeClear={onClearDateRangeFilter}
          onChangeDateRangeFilter={onChangeDateRangeFilter}
          onChangeFilter={onChangeTimeRangeFilter}
          cardButtonClassName={getButtonClassName}
          onCardClick={handleCardClick}
          callMetricData={callMetricData}
          callMetricChartData={metricsChartData}
          isLoading={callMetricsDataLoading}
          isChartLoading={metricsChartDataLoading}
          filterValue={selectedFilter}
          chartParam={chartParam}
          isBayonne={isBayonne}
          isFetched={isFetched}
        />
      ) : (
        <SmsMetric
          dates={dates}
          setDates={setDates}
          onDateRangeClear={onClearDateRangeFilter}
          onChangeDateRangeFilter={onChangeDateRangeFilter}
          onChangeFilter={onChangeTimeRangeFilter}
          cardButtonClassName={getButtonClassName}
          onCardClick={handleSmsCardClick}
          smsMetricData={smsMetricData}
          isLoading={smsMetricsDataLoading}
          smsMetricChartData={smsMetricsChartData}
          isChartLoading={smsMetricsChartDataLoading}
          filterValue={selectedFilter}
          chartParam={smsChartParam}
        />
      )}
      <Flex className={styles.cardContainer} gap={24}>
        <Insights filters={selectedFilter} insightsData={insightsData} isPending={isPending} />
        <AiPerformanceSection
          aiPerformanceData={aiPerformanceData}
          isPending={isAiPerformancePending}
        />
      </Flex>
    </Flex>
  );
}
